import React, { useEffect, useRef, useState } from "react";
import { getFirestore, collection, getDocs, updateDoc, doc } from "firebase/firestore";
import { FiEdit2, FiSave, FiX, FiSlash } from "react-icons/fi";
import useUnsavedChanges from "../hooks/useUnsavedChanges";

// ── Avatar ───────────────────────────────────────────────────────
function Avatar({ name, imageurl, size = 38 }) {
    const [err, setErr] = useState(false);
    const initials = (name || "?").split(" ").map(w => w[0] || "").join("").slice(0, 2).toUpperCase();
    if (imageurl && !err) {
        return (
            <img
                src={imageurl}
                alt={name}
                onError={() => setErr(true)}
                style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", border: "2px solid #E0E0E0", flexShrink: 0 }}
            />
        );
    }
    return (
        <div style={{
            width: size, height: size, borderRadius: "50%", flexShrink: 0,
            background: "#e9c46a", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: size * 0.36, fontWeight: 700,
            fontFamily: "'Quicksand', sans-serif",
        }}>
            {initials}
        </div>
    );
}

// ── Icon button ──────────────────────────────────────────────────
function IconBtn({ icon, color, border, onClick, title, disabled, label }) {
    const ref = useRef(null);
    return (
        <button
            ref={ref}
            title={title}
            onClick={onClick}
            disabled={disabled}
            onMouseDown={() => { if (ref.current && !disabled) { ref.current.style.transform = "translateY(2px)"; ref.current.style.borderBottomWidth = "2px"; } }}
            onMouseUp={() => { if (ref.current) { ref.current.style.transform = ""; ref.current.style.borderBottomWidth = "3px"; } }}
            onMouseLeave={() => { if (ref.current) { ref.current.style.transform = ""; ref.current.style.borderBottomWidth = "3px"; } }}
            style={{
                height: 34, borderRadius: 10, padding: label ? "0 12px" : "0",
                width: label ? "auto" : 34,
                border: `1.5px solid ${border}`, borderBottomWidth: 3,
                background: disabled ? "#eee" : color, color: "#fff",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5,
                cursor: disabled ? "not-allowed" : "pointer", flexShrink: 0,
                fontFamily: "'Quicksand', sans-serif", fontWeight: 700, fontSize: 12,
                transition: "transform 80ms ease, border-bottom-width 80ms ease",
                opacity: disabled ? 0.5 : 1,
            }}
        >
            {icon}{label}
        </button>
    );
}

const inputStyle = {
    fontFamily: "'Quicksand', sans-serif", fontSize: 13, fontWeight: 600,
    padding: "7px 10px", borderRadius: 8,
    border: "1.5px solid #E0E0E0", background: "#f7f9fb",
    width: "100%", outline: "none", color: "#000",
    boxSizing: "border-box",
};

export default function PremiumUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editingUser, setEditingUser] = useState({ name: "", email: "", username: "", imageurl: "" });
    const [saving, setSaving] = useState(false);
    const db = getFirestore();

    // Confirm before leaving while an unsaved inline edit is open.
    useUnsavedChanges(editingId !== null && !saving);

    useEffect(() => {
        const fetch = async () => {
            try {
                const snap = await getDocs(collection(db, "users"));
                const premium = [];
                snap.forEach(d => {
                    const data = d.data();
                    if (data.premium === true) premium.push({ id: d.id, ...data });
                });
                premium.sort((a, b) => (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase()));
                setUsers(premium);
            } catch {
                setError("Could not fetch premium users.");
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [db]);

    useEffect(() => {
        if (!editingId) return;
        const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [editingId]);

    const handleEdit = (user) => {
        setEditingId(user.id);
        setEditingUser({ name: user.name || "", email: user.email || "", username: user.username || "", imageurl: user.imageurl || "" });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        await updateDoc(doc(db, "users", editingId), editingUser);
        setUsers(prev => prev.map(u => u.id === editingId ? { ...u, ...editingUser } : u));
        setEditingId(null);
        setSaving(false);
    };

    const handleCancel = () => { setEditingId(null); };

    const handleRevoke = async (userId, userName) => {
        if (!window.confirm(`Revoke premium from ${userName || "this user"}?`)) return;
        await updateDoc(doc(db, "users", userId), { premium: false });
        setUsers(prev => prev.filter(u => u.id !== userId));
    };

    const set = (key) => (val) => setEditingUser(u => ({ ...u, [key]: val }));

    if (loading) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", gap: 12, color: "#606060", fontWeight: 600, fontFamily: "'Quicksand', sans-serif" }}>
                <div style={{ width: 20, height: 20, border: "2.5px solid #E0E0E0", borderTop: "2.5px solid #e9c46a", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                Loading premium users…
            </div>
        );
    }

    return (
        <div style={{ fontFamily: "'Quicksand', sans-serif", maxWidth: 1100 }}>

            {/* ── Header ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <h1 style={{ margin: 0 }}>Premium Users</h1>
                        <span style={{
                            background: "#e9c46a", color: "#fff",
                            borderRadius: 999, padding: "3px 14px",
                            fontSize: 13, fontWeight: 700,
                        }}>★ {users.length}</span>
                    </div>
                    <div style={{ color: "#606060", fontWeight: 600, fontSize: 14, marginTop: 4 }}>
                        Users with active premium subscriptions
                    </div>
                </div>
            </div>

            {error && (
                <div style={{
                    background: "#fde8e8", border: "1px solid #f5c6c6", borderRadius: 12,
                    padding: "12px 16px", color: "#c0392b", fontWeight: 600, fontSize: 14, marginBottom: 20,
                }}>
                    {error}
                </div>
            )}

            {/* ── Table card ── */}
            <div style={{
                background: "#fff", borderRadius: 20, border: "1px solid #E0E0E0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "auto",
            }}>
                {users.length === 0 ? (
                    <div style={{
                        padding: "56px 32px", textAlign: "center",
                        color: "#aaa", fontWeight: 600, fontSize: 15,
                    }}>
                        No premium users yet.
                    </div>
                ) : (
                    <table style={{ width: "100%", minWidth: 520, borderCollapse: "collapse", fontFamily: "'Quicksand', sans-serif" }}>
                        <thead>
                            <tr style={{ background: "#2a9d8f", color: "#fff" }}>
                                {["", "User", "Username", "Email", "Actions"].map(col => (
                                    <th key={col} style={{
                                        textAlign: "left", fontWeight: 700,
                                        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em",
                                        padding: "12px 16px",
                                    }}>
                                        {col}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user, idx) => {
                                const isEditing = editingId === user.id;
                                const isLast = idx === users.length - 1;
                                const rowBg = isEditing ? "#f0faf9" : idx % 2 === 0 ? "#fff" : "#f7f9fb";
                                return (
                                    <tr
                                        key={user.id}
                                        style={{
                                            background: rowBg,
                                            borderBottom: isLast ? "none" : "1px solid #f0f0f0",
                                            outline: isEditing ? "2px solid #2a9d8f" : "none",
                                            outlineOffset: -1,
                                        }}
                                    >
                                        {/* Avatar */}
                                        <td style={{ padding: "10px 12px 10px 16px", width: 52 }}>
                                            <Avatar
                                                name={isEditing ? editingUser.name : user.name}
                                                imageurl={isEditing ? editingUser.imageurl : user.imageurl}
                                            />
                                        </td>

                                        {/* Name */}
                                        <td style={{ padding: "10px 12px" }}>
                                            {isEditing ? (
                                                <input
                                                    value={editingUser.name}
                                                    onChange={e => set("name")(e.target.value)}
                                                    placeholder="Name"
                                                    style={inputStyle}
                                                    onFocus={e => { e.target.style.borderColor = "#2a9d8f"; }}
                                                    onBlur={e => { e.target.style.borderColor = "#E0E0E0"; }}
                                                />
                                            ) : (
                                                <div>
                                                    <div style={{ fontWeight: 700, fontSize: 14 }}>{user.name || "—"}</div>
                                                    <span style={{
                                                        fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                                                        background: "#e9c46a18", color: "#b8890a",
                                                        border: "1px solid #e9c46a40", borderRadius: 999,
                                                        padding: "1px 7px",
                                                    }}>
                                                        ★ Premium
                                                    </span>
                                                </div>
                                            )}
                                        </td>

                                        {/* Username */}
                                        <td style={{ padding: "10px 12px" }}>
                                            {isEditing ? (
                                                <input
                                                    value={editingUser.username}
                                                    onChange={e => set("username")(e.target.value)}
                                                    placeholder="Username"
                                                    style={inputStyle}
                                                    onFocus={e => { e.target.style.borderColor = "#2a9d8f"; }}
                                                    onBlur={e => { e.target.style.borderColor = "#E0E0E0"; }}
                                                />
                                            ) : (
                                                <span style={{ fontSize: 13, color: "#606060", fontWeight: 600 }}>
                                                    {user.username ? `@${user.username}` : "—"}
                                                </span>
                                            )}
                                        </td>

                                        {/* Email */}
                                        <td style={{ padding: "10px 12px" }}>
                                            {isEditing ? (
                                                <input
                                                    type="email"
                                                    value={editingUser.email}
                                                    onChange={e => set("email")(e.target.value)}
                                                    placeholder="Email"
                                                    style={inputStyle}
                                                    onFocus={e => { e.target.style.borderColor = "#2a9d8f"; }}
                                                    onBlur={e => { e.target.style.borderColor = "#E0E0E0"; }}
                                                />
                                            ) : (
                                                <span style={{ fontSize: 13, color: "#333", fontWeight: 500 }}>
                                                    {user.email || "—"}
                                                </span>
                                            )}
                                        </td>

                                        {/* Actions */}
                                        <td style={{ padding: "10px 16px" }}>
                                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                {isEditing ? (
                                                    <>
                                                        <IconBtn
                                                            icon={<FiSave size={14} />}
                                                            label={saving ? "Saving…" : "Save"}
                                                            color="#2a9d8f" border="#258D80"
                                                            onClick={handleSave}
                                                            disabled={saving}
                                                        />
                                                        <IconBtn
                                                            icon={<FiX size={14} />}
                                                            color="#888" border="#aaa"
                                                            onClick={handleCancel}
                                                            title="Cancel"
                                                            disabled={saving}
                                                        />
                                                    </>
                                                ) : (
                                                    <>
                                                        <IconBtn
                                                            icon={<FiEdit2 size={14} />}
                                                            color="#e9c46a" border="#d4b15f"
                                                            onClick={() => handleEdit(user)}
                                                            title="Edit"
                                                        />
                                                        <IconBtn
                                                            icon={<FiSlash size={14} />}
                                                            label="Revoke"
                                                            color="#e63946" border="#b82d38"
                                                            onClick={() => handleRevoke(user.id, user.name)}
                                                            title="Revoke Premium"
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
